import we from "@arcgis/core/identity/IdentityManager";
import Qr from "@arcgis/core/identity/OAuthInfo";
import Q from "@arcgis/core/portal/Portal";
import { createLogger as x } from "@origo/logger";
import rr from "@arcgis/core/portal/PortalQueryParams";
import me from "@arcgis/core/portal/PortalItem";
import Re from "@arcgis/core/request";
import zr from "@arcgis/core/rest/support/Query";
import * as Xr from "@arcgis/core/rest/query";
import z, { useEffect as $, useRef as Se, useCallback as Hr, useState as M, Component as Zr } from "react";
import { useDispatch as be, useSelector as j } from "react-redux";
import { createAsyncThunk as q, createSlice as Te, combineReducers as et } from "@reduxjs/toolkit";
import { CalciteAlert as rt, CalciteButton as tt } from "@esri/calcite-components-react";
const P = x("AuthService");
class tr {
  constructor(r) {
    this.config = {
      appId: r.appId,
      portalUrl: r.portalUrl || "https://www.arcgis.com",
      flowType: r.flowType || "auto",
      popup: r.popup ?? !1
    };
  }
  /**
   * Initializes OAuth configuration and registers with IdentityManager
   * 
   * @example
   * ```typescript
   * const authService = new AuthService({
   *   appId: '06bJzGl3cUQAR5lX',
   *   portalUrl: 'https://www.arcgis.com'
   * });
   * authService.initialize();
   * ```
   */
  initialize() {
    if (this.oauthInfo) {
      P.debug("Already initialized");
      return;
    }
    this.oauthInfo = new Qr({
      appId: this.config.appId,
      portalUrl: this.config.portalUrl,
      flowType: this.config.flowType,
      popup: this.config.popup
    }), we.registerOAuthInfos([this.oauthInfo]), P.debug(`Initialized OAuth for ${this.config.portalUrl}`);
  }
  /**
   * Checks current sign-in status and retrieves user information
   * 
   * @param requireAuth - If true, forces sign-in if not authenticated
   * @returns Object with sign-in status and user info
   * 
   * @example
   * ```typescript
   * // Check status without forcing sign-in (for public apps)
   * const { isSignedIn, user } = await authService.checkSignInStatus(false);
   * 
   * // Force sign-in (for authenticated apps)
   * const { isSignedIn, user } = await authService.checkSignInStatus(true);
   * ```
   */
  async checkSignInStatus(r = !1) {
    try {
      const t = new Q({ url: this.config.portalUrl });
      if (t.authMode = r ? "immediate" : "auto", await t.load(), t.user) {
        const a = {
          username: t.user.username,
          fullName: t.user.fullName || void 0,
          email: t.user.email || void 0,
          role: t.user.role || void 0,
          thumbnail: t.user.thumbnailUrl || void 0,
          orgUrl: t.url || void 0
        };
        return P.info(`User signed in: ${a.username}`), { isSignedIn: !0, user: a };
      }
      return P.debug("User not signed in"), { isSignedIn: !1 };
    } catch (t) {
      if (P.error("Error checking sign-in status:", t), r)
        throw new Error("Authentication required but sign-in failed");
      return { isSignedIn: !1 };
    }
  }
  /**
   * Forces user sign-in
   * 
   * Used by authenticated apps (e.g., drømmeplan-admin) to require authentication.
   * Will trigger OAuth flow if user is not signed in.
   * 
   * @returns User information after successful sign-in
   * @throws Error if sign-in fails
   * 
   * @example
   * ```typescript
   * try {
   *   const user = await authService.requireSignIn();
   *   console.log('Signed in as:', user.username);
   * } catch (error) {
   *   console.error('Sign-in failed:', error);
   * }
   * ```
   */
  async requireSignIn() {
    const r = await this.checkSignInStatus(!0);
    if (!r.isSignedIn || !r.user)
      throw new Error("Sign-in required but user not authenticated");
    return r.user;
  }
  /**
   * Signs out current user
   * 
   * @example
   * ```typescript
   * await authService.signOut();
   * ```
   */
  async signOut() {
    try {
      await we.destroyCredentials(), P.debug("User signed out");
    } catch (r) {
      throw P.error("Error signing out:", r), r;
    }
  }
  /**
   * Gets current portal instance
   * 
   * @returns Portal instance configured for this auth service
   */
  getPortal() {
    return new Q({ url: this.config.portalUrl });
  }
  /**
   * Gets an authenticated and loaded Portal instance with user context.
   * 
   * Caches the loaded portal for subsequent calls to avoid repeated auth round-trips.
   * Requires that IdentityManager has been initialized with OAuth credentials
   * (i.e., the user has signed in via `checkSignInStatus` or `requireSignIn` first).
   * 
   * @returns Loaded Portal instance with `portal.user` available
   * @throws Error if portal cannot be loaded or user is not authenticated
   * 
   * @example
   * ```typescript
   * const portal = await authService.getAuthenticatedPortal();
   * console.log('Authenticated as:', portal.user.username);
   * ```
   */
  async getAuthenticatedPortal() {
    if (this.cachedPortal?.user)
      return this.cachedPortal;
    const r = new Q({ url: this.config.portalUrl });
    if (r.authMode = "immediate", await r.load(), !r.user)
      throw new Error("Portal loaded but no authenticated user found");
    return this.cachedPortal = r, r;
  }
}
class X extends Error {
  constructor(r, t, a) {
    super(r), this.name = "SettingsError", this.errorType = t, Object.assign(this, a);
  }
  /**
   * Creates user-friendly Norwegian error message
   */
  toUserMessage() {
    switch (this.errorType) {
      case "NOT_FOUND":
        return `Kunne ikke finne innstillinger for ${this.itemName || "applikasjonen"}. Kontakt administrator.`;
      case "NETWORK":
        return "Kunne ikke laste innstillinger på grunn av nettverksfeil. Prøv igjen.";
      case "PARSE":
        return "Innstillingene har ugyldig format. Kontakt administrator.";
      case "UNAUTHORIZED":
        return "Du har ikke tilgang til å laste innstillinger. Kontakt administrator.";
      default:
        return "En ukjent feil oppstod ved lasting av innstillinger. Kontakt administrator.";
    }
  }
}
const te = x("Auth");
let Y = null;
function _e() {
  if (Y !== null)
    return Y;
  const n = window.location.hostname;
  if (n === "localhost" || n === "127.0.0.1") {
    const t = typeof import.meta < "u" && { BASE_URL: "/", MODE: "production", DEV: !1, PROD: !0, SSR: !1 }.VITE_DEV_CUSTOMER, a = t || "mars";
    return t ? te.debug(`Development mode: using VITE_DEV_CUSTOMER='${a}'`) : te.debug(`Development mode: using default customer '${a}'. Set VITE_DEV_CUSTOMER to override.`), Y = a, a;
  }
  const r = n.split(".");
  if (r.length >= 2) {
    const t = r[1];
    return te.debug(`Extracted customer domain from hostname: '${t}'`), Y = t, t;
  }
  return te.warn(`Unexpected hostname format: '${n}'. Using hostname as customer domain.`), Y = n, n;
}
function nt(n) {
  const r = _e();
  return `title:"${n}.tokenservice", tags:"${n}.tokenservice@${r}"`;
}
function st(n) {
  const r = _e();
  return `title:"${n}.settings", tags:"${n}.settings@${r}"`;
}
const A = x("SettingsService");
class nr {
  constructor(r = {}) {
    this.portalUrl = r.portalUrl || "https://www.arcgis.com", this.timeout = r.timeout || 1e4;
  }
  /**
   * Loads app settings from portal item
   * 
   * @param config - Settings configuration
   * @param requireAuth - Whether authentication is required for loading settings
   * @returns Loaded settings or default settings
   * @throws SettingsError if loading fails
   * 
   * @example
   * ```typescript
   * const settingsService = new SettingsService();
   * 
   * try {
   *   const settings = await settingsService.loadSettings({
   *     appName: 'drømmeplan',
   *     defaultSettings: { webmap: 'default-id' }
   *   }, false);
   * } catch (error) {
   *   if (error instanceof SettingsError) {
   *     alert(error.toUserMessage());
   *   }
   * }
   * ```
   */
  async loadSettings(r, t = !1) {
    const a = st(r.appName), l = _e();
    A.debug(`Loading settings: ${a}`);
    let m;
    try {
      const d = new Q({ url: this.portalUrl });
      d.authMode = t ? "immediate" : "anonymous", await d.load();
      const u = new rr({
        query: a
      }), p = new Promise((E, D) => {
        m = setTimeout(() => {
          D(new X(
            `Settings query timed out after ${this.timeout}ms`,
            "NETWORK",
            {
              itemName: a,
              customer: l,
              portalUrl: this.portalUrl,
              technicalDetails: `Query timeout: ${this.timeout}ms`
            }
          ));
        }, this.timeout);
      }), S = await Promise.race([
        d.queryItems(u),
        p
      ]);
      clearTimeout(m);
      const v = S.results;
      if (v.length === 0) {
        A.warn(`Settings item not found: ${a}, will use default settings`);
        return;
      }
      const b = v[0];
      A.debug(`Found settings item: ${b.id}`);
      try {
        const E = await b.fetchData();
        if (!E || typeof E != "object") {
          A.warn(`Settings data has invalid format, using defaults. Expected object, got ${typeof E}`);
          return;
        }
        return A.debug("Settings loaded successfully"), E;
      } catch (E) {
        A.warn("Failed to fetch settings data from item, using defaults:", E);
        return;
      }
    } catch (d) {
      if (clearTimeout(m), d instanceof X) {
        A.warn("Failed to load settings, will use defaults:", d);
        return;
      }
      A.warn("Error loading settings, will use defaults:", d);
      return;
    }
  }
}
const B = x("PortalItemService");
class Ft {
  constructor(r) {
    this.portal = r;
  }
  /**
   * Creates a new Portal item with JSON data
   * 
   * @param data - JSON-serializable data to store
   * @param metadata - Item title, tags, and optional description
   * @returns The ID of the created item
   */
  async createItem(r, t) {
    const a = new me({
      portal: this.portal,
      title: t.title,
      type: t.type || "Web Mapping Application",
      tags: t.tags,
      snippet: t.snippet,
      description: t.description,
      typeKeywords: ["origo", "filter-template"]
    });
    if (!this.portal.user)
      throw new Error("No authenticated user on portal");
    const l = await this.portal.user.addItem({
      item: a,
      data: JSON.stringify(r)
    });
    if (!l?.id)
      throw new Error("Failed to create Portal item - no ID returned");
    return B.debug(`Created item: ${l.id}`), l.id;
  }
  /**
   * Updates an existing Portal item's data and metadata
   * 
   * @param itemId - ID of the item to update
   * @param data - New JSON data
   * @param metadata - Optional updated metadata
   */
  async updateItem(r, t, a) {
    const l = new me({
      portal: this.portal,
      id: r
    });
    await l.load(), a?.title && (l.title = a.title), a?.snippet && (l.snippet = a.snippet), a?.description && (l.description = a.description), a?.tags && (l.tags = a.tags), await l.update({ data: JSON.stringify(t) }), B.debug(`Updated item: ${r}`);
  }
  /**
   * Deletes a Portal item
   * 
   * @param itemId - ID of the item to delete
   */
  async deleteItem(r) {
    if (!this.portal.user)
      throw new Error("No authenticated user on portal");
    const t = `${this.portal.restUrl}/content/users/${this.portal.user.username}/items/${r}/delete`;
    await Re(t, {
      method: "post",
      query: { f: "json" },
      authMode: "immediate"
    }), B.debug(`Deleted item: ${r}`);
  }
  /**
   * Queries Portal items by tags. Returns own items and items shared
   * with the user's groups.
   * 
   * @param tags - Tags to filter by (all must match)
   * @returns Array of item results with their data
   */
  async queryItems(r) {
    const a = `${r.map((u) => `tags:"${u}"`).join(" AND ")} AND type:"Web Mapping Application"`, l = await this.portal.queryItems({
      query: a,
      num: 100,
      sortField: "modified",
      sortOrder: "desc"
    }), m = await Promise.allSettled(
      l.results.map(async (u) => {
        const p = await u.fetchData();
        return {
          id: u.id,
          title: u.title,
          snippet: u.snippet || void 0,
          owner: u.owner,
          created: u.created?.getTime() ?? 0,
          modified: u.modified?.getTime() ?? 0,
          tags: u.tags || [],
          data: p
        };
      })
    ), d = [];
    for (const u of m)
      u.status === "fulfilled" ? d.push(u.value) : B.warn("Failed to fetch data for an item:", u.reason);
    return d;
  }
  /**
   * Shares a Portal item with specified groups
   * 
   * @param itemId - ID of the item to share
   * @param groupIds - Array of group IDs to share with
   */
  async shareToGroups(r, t) {
    if (await new me({
      portal: this.portal,
      id: r
    }).load(), !this.portal.user)
      throw new Error("No authenticated user on portal");
    const l = `${this.portal.restUrl}/content/users/${this.portal.user.username}/items/${r}/share`;
    await Re(l, {
      method: "post",
      query: {
        everyone: !1,
        org: !1,
        groups: t.join(","),
        f: "json"
      },
      authMode: "immediate"
    }), B.debug(`Shared item ${r} with groups: ${t.join(", ")}`);
  }
  /**
   * Gets the authenticated user's Portal groups, useful for showing
   * sharing options in the UI.
   * 
   * @returns Array of groups with id and title
   */
  async getUserGroups() {
    const r = this.portal.user;
    if (!r)
      throw new Error("No authenticated user on portal");
    return (await r.fetchGroups() || []).map((a) => ({
      id: a.id || "",
      title: a.title || ""
    }));
  }
}
const He = x("GdoTokenProvider");
class sr {
  constructor(r, t = {}) {
    this.refreshCallbacks = [], this.isRefreshing = !1, this.appName = r, this.requireAuth = t.requireAuth ?? !1, this.portalUrl = t.portalUrl || "https://www.arcgis.com", this.refreshConfig = {
      refreshBufferMinutes: t.refreshConfig?.refreshBufferMinutes ?? 5,
      maxRetries: t.refreshConfig?.maxRetries ?? 3,
      retryBaseDelay: t.refreshConfig?.retryBaseDelay ?? 30
    };
  }
  async getToken() {
    if (this.currentToken && !this.isExpired())
      return this.currentToken;
    try {
      return await this.fetchTokenFromService();
    } catch (r) {
      He.warn("Failed:", r);
      return;
    }
  }
  async refreshToken() {
    if (this.isRefreshing)
      return new Promise((r, t) => {
        const a = setTimeout(() => {
          t(new Error("Token refresh timed out waiting for in-flight refresh"));
        }, 3e4);
        this.refreshCallbacks.push((l) => {
          clearTimeout(a), l ? r(l) : t(new Error("Token refresh failed"));
        });
      });
    this.isRefreshing = !0;
    try {
      const r = await this.fetchTokenFromService();
      return this.isRefreshing = !1, this.refreshCallbacks.forEach((t) => t(r)), r;
    } catch (r) {
      throw this.isRefreshing = !1, this.refreshCallbacks.forEach((t) => t(void 0)), r;
    }
  }
  getExpiration() {
    return this.expiresAt;
  }
  isExpired() {
    if (!this.expiresAt)
      return !1;
    const r = Date.now(), t = this.refreshConfig.refreshBufferMinutes * 60 * 1e3;
    return r >= this.expiresAt - t;
  }
  onTokenRefreshed(r) {
    this.refreshCallbacks.push(r);
  }
  cleanup() {
    this.refreshTimer && (clearTimeout(this.refreshTimer), this.refreshTimer = void 0), this.refreshCallbacks = [];
  }
  async fetchTokenFromService() {
    const r = nt(this.appName), t = new rr({ query: r }), a = new Q({ url: this.portalUrl });
    this.requireAuth || (a.authMode = "anonymous");
    const m = (await a.queryItems(t)).results;
    if (m.length === 0)
      throw new Error("Token service not found");
    const d = new zr({ where: "1=1", outFields: ["*"], returnGeometry: !1 }), u = await Xr.executeQueryJSON(m[0].url, d);
    if (u.features.length === 0)
      throw new Error("No token in service");
    const p = u.features[0].attributes, S = p.token;
    if (!S)
      throw new Error("Token attribute empty");
    this.currentToken = S;
    const v = p.expires || p.expiresAt || p.expiration;
    return v ? (this.expiresAt = typeof v == "number" ? v : Date.parse(v), this.scheduleRefresh()) : (this.expiresAt = Date.now() + 24 * 60 * 60 * 1e3, this.scheduleRefresh()), S;
  }
  scheduleRefresh() {
    if (this.refreshTimer && clearTimeout(this.refreshTimer), !this.expiresAt)
      return;
    const r = Date.now(), t = this.refreshConfig.refreshBufferMinutes * 60 * 1e3, a = this.expiresAt - t - r;
    if (a <= 0) {
      this.refreshWithRetry();
      return;
    }
    this.refreshTimer = window.setTimeout(() => this.refreshWithRetry(), a);
  }
  async refreshWithRetry(r = 1) {
    try {
      await this.refreshToken();
    } catch {
      if (r < this.refreshConfig.maxRetries) {
        const a = this.refreshConfig.retryBaseDelay * Math.pow(2, r - 1);
        setTimeout(() => this.refreshWithRetry(r + 1), a * 1e3);
      } else
        He.error("Token refresh failed after max retries");
    }
  }
  async queryRoles(r) {
    try {
      return (await Re("https://services.geodataonline.no/arcgis/rest/services/ProxyServices/roles", { query: { token: r } })).data;
    } catch {
      return {};
    }
  }
  async getTokenMetadata() {
    const r = await this.getToken();
    if (!r)
      return;
    const t = await this.queryRoles(r);
    return { token: r, expiresAt: this.expiresAt, username: t.username, customerName: t.customerName, applicationRoles: t.applicationRoles };
  }
}
const it = {
  isSignedIn: !1,
  user: void 0,
  status: "idle",
  error: void 0,
  isAuthRequired: !1
}, ne = q(
  "auth/checkSignInStatus",
  async ({ authService: n, requireAuth: r }, { rejectWithValue: t }) => {
    try {
      return await n.checkSignInStatus(r);
    } catch (a) {
      return t(a.message);
    }
  }
), se = q(
  "auth/requireSignIn",
  async ({ authService: n }, { rejectWithValue: r }) => {
    try {
      return await n.requireSignIn();
    } catch (t) {
      return r(t.message);
    }
  }
), ie = q(
  "auth/signOut",
  async ({ authService: n }, { rejectWithValue: r }) => {
    try {
      await n.signOut();
    } catch (t) {
      return r(t.message);
    }
  }
), ir = Te({
  name: "auth",
  initialState: it,
  reducers: {
    setIsAuthRequired: (n, r) => {
      n.isAuthRequired = r.payload;
    },
    clearAuth: (n) => {
      n.isSignedIn = !1, n.user = void 0, n.status = "idle", n.error = void 0;
    }
  },
  extraReducers: (n) => {
    n.addCase(ne.pending, (r) => {
      r.status = "loading", r.error = void 0;
    }).addCase(ne.fulfilled, (r, t) => {
      r.status = "succeeded", r.isSignedIn = t.payload.isSignedIn, r.user = t.payload.user;
    }).addCase(ne.rejected, (r, t) => {
      r.status = "failed", r.error = t.payload;
    }).addCase(se.pending, (r) => {
      r.status = "loading", r.error = void 0;
    }).addCase(se.fulfilled, (r, t) => {
      r.status = "succeeded", r.isSignedIn = !0, r.user = t.payload;
    }).addCase(se.rejected, (r, t) => {
      r.status = "failed", r.error = t.payload;
    }).addCase(ie.pending, (r) => {
      r.status = "loading";
    }).addCase(ie.fulfilled, (r) => {
      r.status = "idle", r.isSignedIn = !1, r.user = void 0, r.error = void 0;
    }).addCase(ie.rejected, (r, t) => {
      r.status = "failed", r.error = t.payload;
    });
  }
}), { setIsAuthRequired: Ut, clearAuth: Nt } = ir.actions, at = ir.reducer, ot = (n) => n.auth.auth.isSignedIn, ut = (n) => n.auth.auth.user, lt = (n) => n.auth.auth.status, Mt = (n) => n.auth.auth.error, qt = (n) => n.auth.auth.isAuthRequired;
function ar(n, r = !1) {
  const t = be(), a = j(ot), l = j(ut), m = j(lt);
  return $(() => {
    t(ne({ authService: n, requireAuth: r }));
  }, [t, n, r]), { isSignedIn: a, user: l, isLoading: m === "loading", signIn: async () => {
    await t(se({ authService: n })).unwrap();
  }, signOut: async () => {
    await t(ie({ authService: n })).unwrap(), window.location.reload();
  } };
}
const or = x("tokenSlice"), ct = {
  token: void 0,
  expiresAt: void 0,
  isRefreshing: !1,
  lastRefreshError: void 0,
  appName: void 0,
  username: void 0,
  customerName: void 0,
  applicationRoles: []
}, ae = q("token/getToken", async ({ tokenProvider: n }) => {
  try {
    return await n.getTokenMetadata?.();
  } catch (r) {
    or.warn("Failed to get token:", r);
    return;
  }
}), ve = q(
  "token/refreshToken",
  async ({ tokenProvider: n }) => {
    try {
      return await n.refreshToken(), await n.getTokenMetadata?.();
    } catch (r) {
      or.warn("Failed to refresh token:", r);
      return;
    }
  }
), ur = Te({
  name: "token",
  initialState: ct,
  reducers: {
    setToken: (n, r) => {
      n.token = r.payload;
    },
    setTokenMetadata: (n, r) => {
      r.payload.token && (n.token = r.payload.token), r.payload.expiresAt && (n.expiresAt = r.payload.expiresAt), r.payload.username && (n.username = r.payload.username), r.payload.customerName && (n.customerName = r.payload.customerName), r.payload.applicationRoles && (n.applicationRoles = r.payload.applicationRoles);
    },
    setAppName: (n, r) => {
      n.appName = r.payload;
    },
    clearToken: (n) => {
      n.token = void 0, n.expiresAt = void 0, n.lastRefreshError = void 0, n.username = void 0, n.customerName = void 0, n.applicationRoles = [];
    }
  },
  extraReducers: (n) => {
    n.addCase(ae.pending, (r) => {
      r.isRefreshing = !0, r.lastRefreshError = void 0;
    }).addCase(ae.fulfilled, (r, t) => {
      r.isRefreshing = !1, t.payload && (r.token = t.payload.token, r.expiresAt = t.payload.expiresAt, r.username = t.payload.username, r.customerName = t.payload.customerName, r.applicationRoles = t.payload.applicationRoles || []);
    }).addCase(ae.rejected, (r, t) => {
      r.isRefreshing = !1, r.lastRefreshError = t.error?.message;
    }).addCase(ve.pending, (r) => {
      r.isRefreshing = !0, r.lastRefreshError = void 0;
    }).addCase(ve.fulfilled, (r, t) => {
      r.isRefreshing = !1, t.payload && (r.token = t.payload.token, r.expiresAt = t.payload.expiresAt, r.username = t.payload.username, r.customerName = t.payload.customerName, r.applicationRoles = t.payload.applicationRoles || []);
    }).addCase(ve.rejected, (r, t) => {
      r.isRefreshing = !1, r.lastRefreshError = t.error?.message;
    });
  }
}), { setToken: Wt, setTokenMetadata: ft, setAppName: Lt, clearToken: Vt } = ur.actions, dt = ur.reducer, ht = (n) => n.auth.token.token, Yt = (n) => n.auth.token.expiresAt, Bt = (n) => n.auth.token.expiresAt ? Date.now() >= n.auth.token.expiresAt : !1, pt = (n) => n.auth.token.isRefreshing, Kt = (n) => n.auth.token.lastRefreshError, Gt = (n) => n.auth.token.token, Jt = (n) => n.auth.token.applicationRoles, Qt = (n) => n.auth.token.username, zt = (n) => n.auth.token.customerName, gt = x("useTokenProvider");
function lr(n) {
  const r = be(), t = j(ht), a = j(pt), l = Se(!1);
  $(() => (l.current || (l.current = !0, r(ae({ tokenProvider: n })), n.onTokenRefreshed((d) => {
    r(ft({ token: d, expiresAt: n.getExpiration() }));
  })), () => {
    n.cleanup();
  }), [r, n]);
  const m = Hr(async () => {
    try {
      await n.refreshToken();
    } catch (d) {
      gt.warn("Token refresh failed:", d);
    }
  }, [n]);
  return { token: t, isRefreshing: a, refresh: m };
}
const mt = x("settingsSlice"), vt = {
  settings: void 0,
  isLoading: !1
}, Ee = q(
  "settings/loadSettings",
  async ({ settingsService: n, config: r, requireAuth: t }) => {
    try {
      return await n.loadSettings(r, t);
    } catch (a) {
      return mt.warn("Failed to load settings, using defaults:", a), r.defaultSettings;
    }
  }
), cr = Te({
  name: "settings",
  initialState: vt,
  reducers: {
    setSettings: (n, r) => {
      n.settings = r.payload;
    }
  },
  extraReducers: (n) => {
    n.addCase(Ee.pending, (r) => {
      r.isLoading = !0;
    }).addCase(Ee.fulfilled, (r, t) => {
      r.isLoading = !1, r.settings = t.payload;
    });
  }
}), { setSettings: Xt } = cr.actions, yt = cr.reducer, wt = (n) => n.auth.settings.settings, Rt = (n) => n.auth.settings.isLoading;
function fr(n, r, t = !1) {
  const a = be(), l = j(wt), m = j(Rt), d = Se(!1);
  return $(() => {
    d.current || (d.current = !0, a(Ee({ settingsService: n, config: r, requireAuth: t })));
  }, [a, n, t]), { settings: l || r.defaultSettings, isLoading: m };
}
const ye = x("useTokenRegistration");
function Ht(n, r) {
  $(() => {
    if (n) {
      ye.debug(`Registering token for ${r}`);
      try {
        we.registerToken({
          server: r,
          token: n
        }), ye.debug(`Token registered successfully for ${r}`);
      } catch (t) {
        ye.error("Failed to register token:", t);
      }
    }
  }, [n, r]);
}
function Zt(n, r, t) {
  const [a] = M(() => new tr({ appId: "06bJzGl3cUQAR5lX", portalUrl: t })), [l] = M(() => new sr(n, { requireAuth: !1, portalUrl: t })), [m] = M(() => new nr({ portalUrl: t }));
  $(() => {
    a.initialize();
  }, [a]), ar(a, !1);
  const { token: d } = lr(l), { settings: u, isLoading: p } = fr(m, { appName: n, defaultSettings: r }, !1);
  return { token: d, settings: u, isLoading: p };
}
function en(n, r, t) {
  const [a] = M(() => new tr({ appId: "06bJzGl3cUQAR5lX", portalUrl: t })), [l] = M(() => new sr(n, { requireAuth: !0, portalUrl: t })), [m] = M(() => new nr({ portalUrl: t })), d = Se(!1);
  $(() => {
    a.initialize();
  }, [a]);
  const u = ar(a, !0), { token: p } = lr(l), { settings: S, isLoading: v } = fr(m, { appName: n, defaultSettings: r }, !0);
  $(() => {
    !u.isLoading && !u.isSignedIn && !d.current && (d.current = !0, u.signIn());
  }, [u.isLoading, u.isSignedIn]);
  const b = u.isSignedIn && !v && !!p;
  return { isSignedIn: u.isSignedIn, user: u.user, token: p, settings: S, isReady: b, signOut: u.signOut };
}
var ke = { exports: {} }, K = {};
/**
 * @license React
 * react-jsx-runtime.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var Ze;
function Et() {
  if (Ze)
    return K;
  Ze = 1;
  var n = z, r = Symbol.for("react.element"), t = Symbol.for("react.fragment"), a = Object.prototype.hasOwnProperty, l = n.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.ReactCurrentOwner, m = { key: !0, ref: !0, __self: !0, __source: !0 };
  function d(u, p, S) {
    var v, b = {}, E = null, D = null;
    S !== void 0 && (E = "" + S), p.key !== void 0 && (E = "" + p.key), p.ref !== void 0 && (D = p.ref);
    for (v in p)
      a.call(p, v) && !m.hasOwnProperty(v) && (b[v] = p[v]);
    if (u && u.defaultProps)
      for (v in p = u.defaultProps, p)
        b[v] === void 0 && (b[v] = p[v]);
    return { $$typeof: r, type: u, key: E, ref: D, props: b, _owner: l.current };
  }
  return K.Fragment = t, K.jsx = d, K.jsxs = d, K;
}
var G = {};
/**
 * @license React
 * react-jsx-runtime.development.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var er;
function kt() {
  return er || (er = 1, process.env.NODE_ENV !== "production" && function() {
    var n = z, r = Symbol.for("react.element"), t = Symbol.for("react.portal"), a = Symbol.for("react.fragment"), l = Symbol.for("react.strict_mode"), m = Symbol.for("react.profiler"), d = Symbol.for("react.provider"), u = Symbol.for("react.context"), p = Symbol.for("react.forward_ref"), S = Symbol.for("react.suspense"), v = Symbol.for("react.suspense_list"), b = Symbol.for("react.memo"), E = Symbol.for("react.lazy"), D = Symbol.for("react.offscreen"), Ce = Symbol.iterator, dr = "@@iterator";
    function hr(e) {
      if (e === null || typeof e != "object")
        return null;
      var s = Ce && e[Ce] || e[dr];
      return typeof s == "function" ? s : null;
    }
    var F = n.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
    function R(e) {
      {
        for (var s = arguments.length, i = new Array(s > 1 ? s - 1 : 0), o = 1; o < s; o++)
          i[o - 1] = arguments[o];
        pr("error", e, i);
      }
    }
    function pr(e, s, i) {
      {
        var o = F.ReactDebugCurrentFrame, h = o.getStackAddendum();
        h !== "" && (s += "%s", i = i.concat([h]));
        var g = i.map(function(f) {
          return String(f);
        });
        g.unshift("Warning: " + s), Function.prototype.apply.call(console[e], console, g);
      }
    }
    var gr = !1, mr = !1, vr = !1, yr = !1, wr = !1, xe;
    xe = Symbol.for("react.module.reference");
    function Rr(e) {
      return !!(typeof e == "string" || typeof e == "function" || e === a || e === m || wr || e === l || e === S || e === v || yr || e === D || gr || mr || vr || typeof e == "object" && e !== null && (e.$$typeof === E || e.$$typeof === b || e.$$typeof === d || e.$$typeof === u || e.$$typeof === p || // This needs to include all possible module reference object
      // types supported by any Flight configuration anywhere since
      // we don't know which Flight build this will end up being used
      // with.
      e.$$typeof === xe || e.getModuleId !== void 0));
    }
    function Er(e, s, i) {
      var o = e.displayName;
      if (o)
        return o;
      var h = s.displayName || s.name || "";
      return h !== "" ? i + "(" + h + ")" : i;
    }
    function Ae(e) {
      return e.displayName || "Context";
    }
    function C(e) {
      if (e == null)
        return null;
      if (typeof e.tag == "number" && R("Received an unexpected object in getComponentNameFromType(). This is likely a bug in React. Please file an issue."), typeof e == "function")
        return e.displayName || e.name || null;
      if (typeof e == "string")
        return e;
      switch (e) {
        case a:
          return "Fragment";
        case t:
          return "Portal";
        case m:
          return "Profiler";
        case l:
          return "StrictMode";
        case S:
          return "Suspense";
        case v:
          return "SuspenseList";
      }
      if (typeof e == "object")
        switch (e.$$typeof) {
          case u:
            var s = e;
            return Ae(s) + ".Consumer";
          case d:
            var i = e;
            return Ae(i._context) + ".Provider";
          case p:
            return Er(e, e.render, "ForwardRef");
          case b:
            var o = e.displayName || null;
            return o !== null ? o : C(e.type) || "Memo";
          case E: {
            var h = e, g = h._payload, f = h._init;
            try {
              return C(f(g));
            } catch {
              return null;
            }
          }
        }
      return null;
    }
    var O = Object.assign, W = 0, Oe, Ie, Pe, je, $e, De, Fe;
    function Ue() {
    }
    Ue.__reactDisabledLog = !0;
    function kr() {
      {
        if (W === 0) {
          Oe = console.log, Ie = console.info, Pe = console.warn, je = console.error, $e = console.group, De = console.groupCollapsed, Fe = console.groupEnd;
          var e = {
            configurable: !0,
            enumerable: !0,
            value: Ue,
            writable: !0
          };
          Object.defineProperties(console, {
            info: e,
            log: e,
            warn: e,
            error: e,
            group: e,
            groupCollapsed: e,
            groupEnd: e
          });
        }
        W++;
      }
    }
    function Sr() {
      {
        if (W--, W === 0) {
          var e = {
            configurable: !0,
            enumerable: !0,
            writable: !0
          };
          Object.defineProperties(console, {
            log: O({}, e, {
              value: Oe
            }),
            info: O({}, e, {
              value: Ie
            }),
            warn: O({}, e, {
              value: Pe
            }),
            error: O({}, e, {
              value: je
            }),
            group: O({}, e, {
              value: $e
            }),
            groupCollapsed: O({}, e, {
              value: De
            }),
            groupEnd: O({}, e, {
              value: Fe
            })
          });
        }
        W < 0 && R("disabledDepth fell below zero. This is a bug in React. Please file an issue.");
      }
    }
    var oe = F.ReactCurrentDispatcher, ue;
    function H(e, s, i) {
      {
        if (ue === void 0)
          try {
            throw Error();
          } catch (h) {
            var o = h.stack.trim().match(/\n( *(at )?)/);
            ue = o && o[1] || "";
          }
        return `
` + ue + e;
      }
    }
    var le = !1, Z;
    {
      var br = typeof WeakMap == "function" ? WeakMap : Map;
      Z = new br();
    }
    function Ne(e, s) {
      if (!e || le)
        return "";
      {
        var i = Z.get(e);
        if (i !== void 0)
          return i;
      }
      var o;
      le = !0;
      var h = Error.prepareStackTrace;
      Error.prepareStackTrace = void 0;
      var g;
      g = oe.current, oe.current = null, kr();
      try {
        if (s) {
          var f = function() {
            throw Error();
          };
          if (Object.defineProperty(f.prototype, "props", {
            set: function() {
              throw Error();
            }
          }), typeof Reflect == "object" && Reflect.construct) {
            try {
              Reflect.construct(f, []);
            } catch (T) {
              o = T;
            }
            Reflect.construct(e, [], f);
          } else {
            try {
              f.call();
            } catch (T) {
              o = T;
            }
            e.call(f.prototype);
          }
        } else {
          try {
            throw Error();
          } catch (T) {
            o = T;
          }
          e();
        }
      } catch (T) {
        if (T && o && typeof T.stack == "string") {
          for (var c = T.stack.split(`
`), k = o.stack.split(`
`), y = c.length - 1, w = k.length - 1; y >= 1 && w >= 0 && c[y] !== k[w]; )
            w--;
          for (; y >= 1 && w >= 0; y--, w--)
            if (c[y] !== k[w]) {
              if (y !== 1 || w !== 1)
                do
                  if (y--, w--, w < 0 || c[y] !== k[w]) {
                    var _ = `
` + c[y].replace(" at new ", " at ");
                    return e.displayName && _.includes("<anonymous>") && (_ = _.replace("<anonymous>", e.displayName)), typeof e == "function" && Z.set(e, _), _;
                  }
                while (y >= 1 && w >= 0);
              break;
            }
        }
      } finally {
        le = !1, oe.current = g, Sr(), Error.prepareStackTrace = h;
      }
      var N = e ? e.displayName || e.name : "", I = N ? H(N) : "";
      return typeof e == "function" && Z.set(e, I), I;
    }
    function Tr(e, s, i) {
      return Ne(e, !1);
    }
    function _r(e) {
      var s = e.prototype;
      return !!(s && s.isReactComponent);
    }
    function ee(e, s, i) {
      if (e == null)
        return "";
      if (typeof e == "function")
        return Ne(e, _r(e));
      if (typeof e == "string")
        return H(e);
      switch (e) {
        case S:
          return H("Suspense");
        case v:
          return H("SuspenseList");
      }
      if (typeof e == "object")
        switch (e.$$typeof) {
          case p:
            return Tr(e.render);
          case b:
            return ee(e.type, s, i);
          case E: {
            var o = e, h = o._payload, g = o._init;
            try {
              return ee(g(h), s, i);
            } catch {
            }
          }
        }
      return "";
    }
    var L = Object.prototype.hasOwnProperty, Me = {}, qe = F.ReactDebugCurrentFrame;
    function re(e) {
      if (e) {
        var s = e._owner, i = ee(e.type, e._source, s ? s.type : null);
        qe.setExtraStackFrame(i);
      } else
        qe.setExtraStackFrame(null);
    }
    function Cr(e, s, i, o, h) {
      {
        var g = Function.call.bind(L);
        for (var f in e)
          if (g(e, f)) {
            var c = void 0;
            try {
              if (typeof e[f] != "function") {
                var k = Error((o || "React class") + ": " + i + " type `" + f + "` is invalid; it must be a function, usually from the `prop-types` package, but received `" + typeof e[f] + "`.This often happens because of typos such as `PropTypes.function` instead of `PropTypes.func`.");
                throw k.name = "Invariant Violation", k;
              }
              c = e[f](s, f, o, i, null, "SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED");
            } catch (y) {
              c = y;
            }
            c && !(c instanceof Error) && (re(h), R("%s: type specification of %s `%s` is invalid; the type checker function must return `null` or an `Error` but returned a %s. You may have forgotten to pass an argument to the type checker creator (arrayOf, instanceOf, objectOf, oneOf, oneOfType, and shape all require an argument).", o || "React class", i, f, typeof c), re(null)), c instanceof Error && !(c.message in Me) && (Me[c.message] = !0, re(h), R("Failed %s type: %s", i, c.message), re(null));
          }
      }
    }
    var xr = Array.isArray;
    function ce(e) {
      return xr(e);
    }
    function Ar(e) {
      {
        var s = typeof Symbol == "function" && Symbol.toStringTag, i = s && e[Symbol.toStringTag] || e.constructor.name || "Object";
        return i;
      }
    }
    function Or(e) {
      try {
        return We(e), !1;
      } catch {
        return !0;
      }
    }
    function We(e) {
      return "" + e;
    }
    function Le(e) {
      if (Or(e))
        return R("The provided key is an unsupported type %s. This value must be coerced to a string before before using it here.", Ar(e)), We(e);
    }
    var V = F.ReactCurrentOwner, Ir = {
      key: !0,
      ref: !0,
      __self: !0,
      __source: !0
    }, Ve, Ye, fe;
    fe = {};
    function Pr(e) {
      if (L.call(e, "ref")) {
        var s = Object.getOwnPropertyDescriptor(e, "ref").get;
        if (s && s.isReactWarning)
          return !1;
      }
      return e.ref !== void 0;
    }
    function jr(e) {
      if (L.call(e, "key")) {
        var s = Object.getOwnPropertyDescriptor(e, "key").get;
        if (s && s.isReactWarning)
          return !1;
      }
      return e.key !== void 0;
    }
    function $r(e, s) {
      if (typeof e.ref == "string" && V.current && s && V.current.stateNode !== s) {
        var i = C(V.current.type);
        fe[i] || (R('Component "%s" contains the string ref "%s". Support for string refs will be removed in a future major release. This case cannot be automatically converted to an arrow function. We ask you to manually fix this case by using useRef() or createRef() instead. Learn more about using refs safely here: https://reactjs.org/link/strict-mode-string-ref', C(V.current.type), e.ref), fe[i] = !0);
      }
    }
    function Dr(e, s) {
      {
        var i = function() {
          Ve || (Ve = !0, R("%s: `key` is not a prop. Trying to access it will result in `undefined` being returned. If you need to access the same value within the child component, you should pass it as a different prop. (https://reactjs.org/link/special-props)", s));
        };
        i.isReactWarning = !0, Object.defineProperty(e, "key", {
          get: i,
          configurable: !0
        });
      }
    }
    function Fr(e, s) {
      {
        var i = function() {
          Ye || (Ye = !0, R("%s: `ref` is not a prop. Trying to access it will result in `undefined` being returned. If you need to access the same value within the child component, you should pass it as a different prop. (https://reactjs.org/link/special-props)", s));
        };
        i.isReactWarning = !0, Object.defineProperty(e, "ref", {
          get: i,
          configurable: !0
        });
      }
    }
    var Ur = function(e, s, i, o, h, g, f) {
      var c = {
        // This tag allows us to uniquely identify this as a React Element
        $$typeof: r,
        // Built-in properties that belong on the element
        type: e,
        key: s,
        ref: i,
        props: f,
        // Record the component responsible for creating this element.
        _owner: g
      };
      return c._store = {}, Object.defineProperty(c._store, "validated", {
        configurable: !1,
        enumerable: !1,
        writable: !0,
        value: !1
      }), Object.defineProperty(c, "_self", {
        configurable: !1,
        enumerable: !1,
        writable: !1,
        value: o
      }), Object.defineProperty(c, "_source", {
        configurable: !1,
        enumerable: !1,
        writable: !1,
        value: h
      }), Object.freeze && (Object.freeze(c.props), Object.freeze(c)), c;
    };
    function Nr(e, s, i, o, h) {
      {
        var g, f = {}, c = null, k = null;
        i !== void 0 && (Le(i), c = "" + i), jr(s) && (Le(s.key), c = "" + s.key), Pr(s) && (k = s.ref, $r(s, h));
        for (g in s)
          L.call(s, g) && !Ir.hasOwnProperty(g) && (f[g] = s[g]);
        if (e && e.defaultProps) {
          var y = e.defaultProps;
          for (g in y)
            f[g] === void 0 && (f[g] = y[g]);
        }
        if (c || k) {
          var w = typeof e == "function" ? e.displayName || e.name || "Unknown" : e;
          c && Dr(f, w), k && Fr(f, w);
        }
        return Ur(e, c, k, h, o, V.current, f);
      }
    }
    var de = F.ReactCurrentOwner, Be = F.ReactDebugCurrentFrame;
    function U(e) {
      if (e) {
        var s = e._owner, i = ee(e.type, e._source, s ? s.type : null);
        Be.setExtraStackFrame(i);
      } else
        Be.setExtraStackFrame(null);
    }
    var he;
    he = !1;
    function pe(e) {
      return typeof e == "object" && e !== null && e.$$typeof === r;
    }
    function Ke() {
      {
        if (de.current) {
          var e = C(de.current.type);
          if (e)
            return `

Check the render method of \`` + e + "`.";
        }
        return "";
      }
    }
    function Mr(e) {
      {
        if (e !== void 0) {
          var s = e.fileName.replace(/^.*[\\\/]/, ""), i = e.lineNumber;
          return `

Check your code at ` + s + ":" + i + ".";
        }
        return "";
      }
    }
    var Ge = {};
    function qr(e) {
      {
        var s = Ke();
        if (!s) {
          var i = typeof e == "string" ? e : e.displayName || e.name;
          i && (s = `

Check the top-level render call using <` + i + ">.");
        }
        return s;
      }
    }
    function Je(e, s) {
      {
        if (!e._store || e._store.validated || e.key != null)
          return;
        e._store.validated = !0;
        var i = qr(s);
        if (Ge[i])
          return;
        Ge[i] = !0;
        var o = "";
        e && e._owner && e._owner !== de.current && (o = " It was passed a child from " + C(e._owner.type) + "."), U(e), R('Each child in a list should have a unique "key" prop.%s%s See https://reactjs.org/link/warning-keys for more information.', i, o), U(null);
      }
    }
    function Qe(e, s) {
      {
        if (typeof e != "object")
          return;
        if (ce(e))
          for (var i = 0; i < e.length; i++) {
            var o = e[i];
            pe(o) && Je(o, s);
          }
        else if (pe(e))
          e._store && (e._store.validated = !0);
        else if (e) {
          var h = hr(e);
          if (typeof h == "function" && h !== e.entries)
            for (var g = h.call(e), f; !(f = g.next()).done; )
              pe(f.value) && Je(f.value, s);
        }
      }
    }
    function Wr(e) {
      {
        var s = e.type;
        if (s == null || typeof s == "string")
          return;
        var i;
        if (typeof s == "function")
          i = s.propTypes;
        else if (typeof s == "object" && (s.$$typeof === p || // Note: Memo only checks outer props here.
        // Inner props are checked in the reconciler.
        s.$$typeof === b))
          i = s.propTypes;
        else
          return;
        if (i) {
          var o = C(s);
          Cr(i, e.props, "prop", o, e);
        } else if (s.PropTypes !== void 0 && !he) {
          he = !0;
          var h = C(s);
          R("Component %s declared `PropTypes` instead of `propTypes`. Did you misspell the property assignment?", h || "Unknown");
        }
        typeof s.getDefaultProps == "function" && !s.getDefaultProps.isReactClassApproved && R("getDefaultProps is only used on classic React.createClass definitions. Use a static property named `defaultProps` instead.");
      }
    }
    function Lr(e) {
      {
        for (var s = Object.keys(e.props), i = 0; i < s.length; i++) {
          var o = s[i];
          if (o !== "children" && o !== "key") {
            U(e), R("Invalid prop `%s` supplied to `React.Fragment`. React.Fragment can only have `key` and `children` props.", o), U(null);
            break;
          }
        }
        e.ref !== null && (U(e), R("Invalid attribute `ref` supplied to `React.Fragment`."), U(null));
      }
    }
    var ze = {};
    function Xe(e, s, i, o, h, g) {
      {
        var f = Rr(e);
        if (!f) {
          var c = "";
          (e === void 0 || typeof e == "object" && e !== null && Object.keys(e).length === 0) && (c += " You likely forgot to export your component from the file it's defined in, or you might have mixed up default and named imports.");
          var k = Mr(h);
          k ? c += k : c += Ke();
          var y;
          e === null ? y = "null" : ce(e) ? y = "array" : e !== void 0 && e.$$typeof === r ? (y = "<" + (C(e.type) || "Unknown") + " />", c = " Did you accidentally export a JSX literal instead of a component?") : y = typeof e, R("React.jsx: type is invalid -- expected a string (for built-in components) or a class/function (for composite components) but got: %s.%s", y, c);
        }
        var w = Nr(e, s, i, h, g);
        if (w == null)
          return w;
        if (f) {
          var _ = s.children;
          if (_ !== void 0)
            if (o)
              if (ce(_)) {
                for (var N = 0; N < _.length; N++)
                  Qe(_[N], e);
                Object.freeze && Object.freeze(_);
              } else
                R("React.jsx: Static children should always be an array. You are likely explicitly calling React.jsxs or React.jsxDEV. Use the Babel transform instead.");
            else
              Qe(_, e);
        }
        if (L.call(s, "key")) {
          var I = C(e), T = Object.keys(s).filter(function(Jr) {
            return Jr !== "key";
          }), ge = T.length > 0 ? "{key: someKey, " + T.join(": ..., ") + ": ...}" : "{key: someKey}";
          if (!ze[I + ge]) {
            var Gr = T.length > 0 ? "{" + T.join(": ..., ") + ": ...}" : "{}";
            R(`A props object containing a "key" prop is being spread into JSX:
  let props = %s;
  <%s {...props} />
React keys must be passed directly to JSX without using spread:
  let props = %s;
  <%s key={someKey} {...props} />`, ge, I, Gr, I), ze[I + ge] = !0;
          }
        }
        return e === a ? Lr(w) : Wr(w), w;
      }
    }
    function Vr(e, s, i) {
      return Xe(e, s, i, !0);
    }
    function Yr(e, s, i) {
      return Xe(e, s, i, !1);
    }
    var Br = Yr, Kr = Vr;
    G.Fragment = a, G.jsx = Br, G.jsxs = Kr;
  }()), G;
}
process.env.NODE_ENV === "production" ? ke.exports = Et() : ke.exports = kt();
var J = ke.exports;
const St = x("SettingsErrorBoundary");
class rn extends Zr {
  constructor(r) {
    super(r), this.handleRetry = () => {
      this.setState({ error: void 0 }), this.props.onRetry ? this.props.onRetry() : window.location.reload();
    }, this.state = { error: void 0 };
  }
  static getDerivedStateFromError(r) {
    return r instanceof X ? { error: r } : { error: void 0 };
  }
  componentDidCatch(r, t) {
    r instanceof X && St.error("Settings error:", r, t);
  }
  render() {
    return this.state.error ? /* @__PURE__ */ J.jsx("div", { style: { padding: "2rem", maxWidth: "600px", margin: "0 auto" }, children: /* @__PURE__ */ J.jsxs(rt, { open: !0, kind: "danger", autoClose: !1, children: [
      /* @__PURE__ */ J.jsx("div", { slot: "title", children: "Kunne ikke laste innstillinger" }),
      /* @__PURE__ */ J.jsx("div", { slot: "message", children: this.state.error.toUserMessage() }),
      /* @__PURE__ */ J.jsx(tt, { slot: "actions-end", onClick: this.handleRetry, children: "Prøv igjen" })
    ] }) }) : this.props.children;
  }
}
function tn() {
  const [n, r] = z.useState(), t = z.useCallback((l) => {
    l instanceof X && r(l);
  }, []), a = z.useCallback(() => {
    r(void 0);
  }, []);
  return { error: n, handleError: t, clearError: a };
}
const nn = et({
  auth: at,
  token: dt,
  settings: yt
});
export {
  tr as AuthService,
  sr as GdoTokenProvider,
  Ft as PortalItemService,
  X as SettingsError,
  rn as SettingsErrorBoundary,
  nr as SettingsService,
  nn as authCombinedReducer,
  at as authReducer,
  st as buildSettingsQueryString,
  nt as buildTokenServiceQueryString,
  ne as checkSignInStatus,
  Nt as clearAuth,
  Vt as clearToken,
  _e as getCustomerDomain,
  ae as getToken,
  Ee as loadSettings,
  ve as refreshToken,
  se as requireSignIn,
  Jt as selectApplicationRoles,
  Mt as selectAuthError,
  lt as selectAuthStatus,
  zt as selectGdoCustomerName,
  Gt as selectGdoToken,
  Qt as selectGdoUsername,
  qt as selectIsAuthRequired,
  ot as selectIsSignedIn,
  Bt as selectIsTokenExpired,
  pt as selectIsTokenRefreshing,
  wt as selectSettings,
  Rt as selectSettingsLoading,
  ht as selectToken,
  Kt as selectTokenError,
  Yt as selectTokenExpiration,
  ut as selectUser,
  Lt as setAppName,
  Ut as setIsAuthRequired,
  Xt as setSettings,
  Wt as setToken,
  ft as setTokenMetadata,
  yt as settingsReducer,
  ie as signOut,
  dt as tokenReducer,
  ar as useAuth,
  en as useAuthenticatedApp,
  Zt as usePublicApp,
  fr as useSettings,
  tn as useSettingsErrorHandler,
  lr as useTokenProvider,
  Ht as useTokenRegistration
};
//# sourceMappingURL=index.mjs.map
